import * as React from 'react';
import { useSearchParams } from 'react-router-dom';
import { GridActionsCellItem } from '@mui/x-data-grid';
import makeStyles from "@mui/styles/makeStyles";
import Box from '@mui/material/Box';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import {
   LinearProgress, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Typography,
  IconButton, Menu
} from '@mui/material';
import Filter from './Filter';
import { useList } from "hooks/useList";
import Session from 'utils/Session';
import useEditorStore from 'editors/TemplateEditor/editorStore';
import Row from './Tables/Row';
import TableNoRowsOverlay from './TableNoRowsOverlay';
import CustomMenu from './CustomMenu';
import Pagination from './Pagination';
import { IconButton as CustomIconButton } from './CustomButtons';
import theme from 'theme';
import { DebouncedTextField } from './CustomFields';
import { exportShortlistToCsvDynamic } from 'utils';
import CustomButton from 'components/CustomButtons';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import Chip from './Chip';
import IosShareOutlinedIcon from '@mui/icons-material/IosShareOutlined';

const useStyles = makeStyles((theme) => ({
  tableHeader: { 
    backgroundColor: theme.palette.shades['clr-white-900'], 
    color: theme.palette.shades['clr-black-900'], 
    fontWeight: 600 
  },
  iconButtonRoot: {
    color: theme.palette.shades['clr-white-900'],
    '&:hover': {
      backgroundColor: "#a8a3a347"
    },
  },
  headerRow: {
    height: '40px',
    padding: 0,
    '& td': {
      whiteSpace: 'nowrap',
      padding: theme.spacing(1.5,0)
    }
  },
  emptyDialogBox: {
    width: "100%",
    height: '300px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '30px 0px',
  },
  filterItems: {
    width: '100%', 
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  colContent: {
    display: 'flex', 
    gap: theme.spacing(1), alignItems: 'center'
  },
  paginationRoot: {
    display: 'flex', justifyContent: 'space-between', 
    marginTop: theme.spacing(5)
  },
  tableCell: {
    whiteSpace: 'nowrap',
    backgroundColor: theme.palette.neutral['clr-100'],
  },
}));

const sx = {
  stickyColumn: {
    position: "sticky",
    zIndex: 10,
    padding: '0px',
    paddingLeft: '5px'
  },
  actionColumn: {
    whiteSpace: 'nowrap',
    position: "sticky",
    right: '-1px',
    zIndex: 10,
    display: 'flex-end',
    width: '40px',
  },
  analyticsColumn: {
    whiteSpace: 'nowrap',
    position: "sticky",
    right: '0px',
    zIndex: 10,
  },
  leftFixedColumn: {
    whiteSpace: 'nowrap',
    position: "sticky",
    left: '180px', // Position after name column (180px) + some padding
    zIndex: 10,
    backgroundColor: '#fff', // Ensure background is white
  },
  sortIcon: {
    fontSize: '15px',
    cursor: 'pointer',
    display: 'flex',
    color: '#135EBF'
  },
}

export const getListActions = ({
  params, editItem, setActiveItemId, setArchiveItemData, entity, editActionIcon, customActions,
  hideEditAction, hideDeleteAction, editActionLabel, rowSelection, hideArchiveAction = true,
  personalizedList, rowSelectionModel = []
}) => {
  let actions = [];
  if (Array.isArray(customActions)) {
    actions = customActions.map(ca => (
      <GridActionsCellItem
        title={ca.label}
        icon={ca.renderIcon(params)}
        label={ca.label}
        onClick={() => ca.onClick(params)}
      />
    ))
  }

  if (!hideEditAction && editItem && !personalizedList && Session?.permissions?.[entity]?.UPDATE) {
    actions.push(<GridActionsCellItem
      title={editActionLabel || 'Edit'}
      icon={editActionIcon || <CustomIconButton
                                variant='outlined'
                                style={{
                                          border : `1px solid ${theme.palette.primary.main}`,
                                          padding : theme.spacing(1)
                                      }}
                              >
                                <img src='https://assets.languify.in/images/edit-3.svg' alt='edit'/>
                              </CustomIconButton>}
      label={editActionLabel || "Edit"}
      onClick={(e) => editItem(e, params.id)}
    />);
  }

  if (
    !hideDeleteAction && setActiveItemId &&
    Session?.permissions?.[entity]?.DELETE
  ) {
    actions.push(<GridActionsCellItem
      title='Delete'
      icon={<DeleteIcon color='error' />}
      label="Delete"
      onClick={() => setActiveItemId(params.id)}
    />);
  }

  if (rowSelection) {
    actions.push(
      <GridActionsCellItem
        title='Select'
        icon={(
          <input type='checkbox' name={params?.row?.name} value={params?.id}
            checked={rowSelectionModel.includes(params?.id)} height={'100%'} width={'100%'}
            readOnly={true}
          />
        )}
        label="Select"
        sx={{ padding: '0px' }}
      />
    )
  }

  return actions;
}

const SortList = ({ column, handleSort }) => {
  const classes = useStyles();
  const [sortOrder, setSortOrder] = React.useState(false);

  const handleSorting = () => {
    setSortOrder(!sortOrder);
    handleSort([{ field: column?.field, sort: sortOrder ? "desc" : "asc", sortBy: column?.sortBy }])
  }

  return (
    <>
      {
        sortOrder
          ?
          <IconButton size='small' classes={{ root: classes.iconButtonRoot }}>
            <ArrowDownwardIcon onClick={handleSorting} sx={sx.sortIcon} />
          </IconButton>
          :
          <IconButton size='small' classes={{ root: classes.iconButtonRoot }}>
            <ArrowUpwardIcon onClick={handleSorting} sx={sx.sortIcon} />
          </IconButton>
      }
    </>
  )
}

const getMenuListActions = ({ params, menuOptions, archiveItem }) => {
  const options = [];

  menuOptions.forEach(({ label, id, onClick }) => {
    let _onClick = onClick;
    if (['archive', 'unarchive'].includes(id)) _onClick = () => archiveItem(params.id);
    if (['edit', 'duplicate', 'setting'].includes(id) && params?.row?.type === 'follow_up') return;

    options.push({
      label,
      onClick: (event) => {
        event.stopPropagation(); 
        if (_onClick) _onClick(event , params);
      },
    });
  });

  return <CustomMenu params={params} options={options} />;
};

// Extracted TableSection subcomponent
function TableSection({
  _columns, items, showDummyRows, dummyRows, actions, handleRowSelection, showRowSelectCheckbox, rowSelectionModel, onRowClick, appliedFilters, noRowsMessage, loading, total, pageSize, setPageNo, pageNo, height, classes, sx, pageFilters, pathname, handleSortModelChange }) {
  return (
    <Box py={4} style={{ width: '100%' }}>
      <Box style={{ visibility: loading ? 'visible' : 'hidden', padding: '0px 0.5px' }}>
        <LinearProgress />
      </Box>
      <TableContainer sx={{ border: '1px solid #E1E2E3', minHeight: height}}>
        <Table stickyHeader size="small" aria-label="a dense table">
          {/* table header */}
          <TableHead>
            <TableRow className={classes.headerRow} >
              {_columns?.length &&
                _columns?.map((col, index) => (
                  <TableCell
                    className={classes.tableCell}
                    align={col?.align || 'start'}
                    key={index}
                    sx={
                      col?.type === 'actions'
                        ? sx.actionColumn
                        : index === 0
                          ? { ...(sx.stickyColumn), left: 0 }
                          : (index === 1 && showRowSelectCheckbox)
                            ? { ...(sx.stickyColumn), left: '25px' } 
                            : col?.fixed && col?.field === 'overallScore'
                              ? { ...(sx.leftFixedColumn) }
                              : col?.fixed
                                ? { ...(sx.analyticsColumn) } 
                                : null 
                    }
                    style={{
                      minWidth: col?.minWidth,
                      maxWidth: col?.maxWidth,
                    }}
                  >
                    <Box
                      className={classes.colContent}
                      justifyContent={col?.align || 'start'}
                    >
                      <Typography variant='body01-bold'>
                        {col?.label}
                      </Typography>
                      <span>
                        {!col?.disableSorting &&
                          <SortList column={col} handleSort={handleSortModelChange} />
                        }
                      </span>
                    </Box>
                  </TableCell>
                ))
              }
            </TableRow>
          </TableHead>
          {/* table rows */}
          <TableBody>
            {showDummyRows && items.length === 0 ?
              dummyRows.map((item, index) => (
                <Row
                  key={index}
                  rowData={item}
                  index={index}
                  columns={_columns}
                  getActions={actions}
                />
              ))
              :
              items.map((item, index) => (
                <Row
                  key={index}
                  rowData={item}
                  rowNo={index}
                  columns={_columns}
                  getActions={actions}
                  handleRowSelection={handleRowSelection}
                  showRowSelectCheckbox={showRowSelectCheckbox}
                  rowSelectionModel={rowSelectionModel}
                  onRowClick={onRowClick}
                />
              ))
            }
          </TableBody>
        </Table>
        {items.length === 0 && !showDummyRows ?
          <Box className={classes.emptyDialogBox} >
            <TableNoRowsOverlay
              message={
                appliedFilters.domain || appliedFilters.batch
                  ? 'Nothing to show for applied filters!'
                  : noRowsMessage
              }
            />
          </Box>
          : null
        }
      </TableContainer>
      <Box className={classes.paginationRoot}>
        <Typography variant='h6-medium' color='neutral.clr-500' display='flex' gap='4px'>
          Showing 
          <Typography variant='h6-medium' color='neutral.clr-900'>{items?.length || 0}</Typography> of 
          <Typography  variant='h6-medium' color='neutral.clr-900'>{total}</Typography> entries
        </Typography>
        <Stack spacing={2} direction='row' alignItems='center'>
          <Pagination
            count={Math.max(1, Math.ceil(total / pageSize))}
            setPage={setPageNo}
            page={pageNo}
          />
        </Stack>
      </Box>
    </Box>
  );
}

export default function DynamicTable({
  width='100%', entity, columns, useListProps, actions, hideEditAction, hideDeleteAction, hideArchiveAction,
  noRowsMessage, editActionLabel, showRowSelectCheckbox = false, filters = [], menuOptions,
  filterOptions = {}, editActionIcon = null, height = '345px',
  quickSearchField = 'name', onRowClick = () => { }, refreshData = false,
  dummyRows = [], showDummyRows = false, rowSelectionModel = [], setRefresh = () => {}, setRowSelectionModel = () => { },
  personalizedList,enableCsvExport = false, onCsvExportSuccess = () => {},
  hideColumnSelection = true, 
  onExport = null,
  ...props
}) {
  const classes = useStyles();
  const [searchParams] = useSearchParams();
  const setPageFilters = useEditorStore((state) => state.setPageFilters);
  const pageFilters = useEditorStore((state) => state.pageFilters);
  const pathname = window.location.pathname;

  // New: visibleColumns state for category logic
  const [visibleColumns, setVisibleColumns] = React.useState(() => columns?.map(col => col.id || col.field));
  // Some callers rebuild `columns` inline on every render, so the reset effect
  // below keys off the field list itself instead of the array identity.
  const columnFieldsKey = (columns || []).map(col => col.id || col.field).join('|');
  const [categoryAnchorEl, setCategoryAnchorEl] = React.useState(null);
  const categoryOpen = Boolean(categoryAnchorEl);
  
  // First 6 columns are always required and cannot be removed
  const REQUIRED_COLUMNS_COUNT = 6;

  // Export popup states
  const [exportAnchorEl, setExportAnchorEl] = React.useState(null);
  const exportOpen = Boolean(exportAnchorEl);
  const [exportColumns, setExportColumns] = React.useState(() => columns?.map(col => col.id || col.field) || []);

  // New: All columns are selected by default as filters. The export selection
  // has to follow the same reset: switching interview variant swaps the whole
  // column set, and a selection left over from the previous variant silently
  // drops every field the two do not share from the exported sheet.
  React.useEffect(() => {
    const fields = (columns || []).map(col => col.id || col.field);
    setVisibleColumns(fields);
    setExportColumns(fields);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnFieldsKey]);

  // Only show columns that are in visibleColumns
  const filteredColumns = React.useMemo(() => {
    if (!columns?.length) return [];
    return columns.filter(col => visibleColumns.includes(col.id || col.field));
  }, [columns, visibleColumns]);

  // Applied filter chips for columns
  const appliedColumnChips = filteredColumns.map(col => ({
    id: col.id || col.field,
    label: col.label,
  }));

  const handleRemoveColumn = (colId) => {
    // Don't allow removal of first 6 columns
    const colIndex = columns.findIndex(col => (col.id || col.field) === colId);
    if (colIndex < REQUIRED_COLUMNS_COUNT) {
      return; // Cannot remove required columns
    }
    setVisibleColumns(visibleColumns.filter(id => id !== colId));
  };

  const handleClearAllColumns = () => {
    // Keep the first 6 columns always visible
    const requiredColumns = columns.slice(0, REQUIRED_COLUMNS_COUNT).map(col => col.id || col.field);
    setVisibleColumns(requiredColumns);
  };

  // Category button label logic
  let categoryLabel = 'Category';
  if (filteredColumns.length > 1) {
    categoryLabel = 'Category: Multiple';
  } else if (filteredColumns.length === 1) {
    categoryLabel = `Category: ${filteredColumns[0].label}`;
  }

  const handleCategoryClick = (event) => {
    setCategoryAnchorEl(event.currentTarget);
  };
  const handleCategoryClose = () => {
    setCategoryAnchorEl(null);
  };
  const handleCategoryToggle = (field) => {
    // Don't allow toggling of first 6 columns
    const colIndex = columns.findIndex(col => (col.id || col.field) === field);
    if (colIndex < REQUIRED_COLUMNS_COUNT) {
      return; // Cannot toggle required columns
    }
    
    if (visibleColumns.includes(field)) {
      setVisibleColumns(visibleColumns.filter(f => f !== field));
    } else {
      setVisibleColumns([...visibleColumns, field]);
    }
  };

  // Export popup handlers
  const handleExportClick = (event) => {
    setExportAnchorEl(event.currentTarget);
  };
  const handleExportClose = () => {
    setExportAnchorEl(null);
  };
  const handleExportToggle = (field) => {
    if (exportColumns.includes(field)) {
      setExportColumns(exportColumns.filter(f => f !== field));
    } else {
      setExportColumns([...exportColumns, field]);
    }
  };
  const handleExportSubmit = () => {
    if (onExport && exportColumns.length > 0) {
      const selectedCols = columns.filter(col => exportColumns.includes(col.id || col.field));
      onExport(selectedCols);
    }
    handleExportClose();
  };

  const {
    loading, items, setItems, total, editItem, deleteItem, archiveItem, setFilters, setSortModel,
    setPageNo, pageNo, pageSize, filters: appliedFilters, refresh
  } = useList({ initialPageNo: 1, initialPageSize: 10, ...useListProps });

  React.useEffect(() => {
    if (enableCsvExport && items.length > 0) {
      try {
        exportShortlistToCsvDynamic(columns, items );
        onCsvExportSuccess();
      } catch (error) {
        console.error('CSV Export failed:', error);
      }
    }
  }, [enableCsvExport]);

  const handleFilterChange = ({ field, value }) => {
    // Any filter change re-scopes the result set, so the current page number is
    // meaningless against it — searching from page 3 would ask the server for
    // page 3 of the matches and usually render an empty table.
    setPageNo(1);

    if (value) {
      setFilters({ ...appliedFilters, [field]: value });
      
      setPageFilters(
        { [pathname]: { ...appliedFilters, [field]: value } }
      );
    }
    else {
      setFilters((filters) => {
        const newFilters = { ...filters };

        newFilters[field] = undefined;
        setPageFilters({ [pathname]: newFilters });

        return newFilters;
      });
    }
  };

  const handleSortModelChange = (newModel) => {
    if (newModel[0]) {
      const { field, sort, sortBy } = newModel[0];
      setSortModel({ orderBy: sortBy? sortBy : field, order: sort });
    }
  };

  const handleRowSelection = (data) => {
    if (rowSelectionModel?.includes(data._id)) {
      setRowSelectionModel(rowSelectionModel.filter(r => r !== data._id));
    }
    else {
      setRowSelectionModel([...rowSelectionModel, data._id]);
    }
  }

  const handleSelectAll = () => {
    if (rowSelectionModel.length === items.length) {
      setRowSelectionModel([]);
    } else {
      const allRowIds = items.map((row) => row._id);
      setRowSelectionModel(allRowIds);
    }
  };

  const _columns = React.useMemo(() => {
    if (!filteredColumns?.length) return [];
    const allColumns = filteredColumns?.map(({ id, ...c }) => ({
      flex: 1,
      headerClassName: classes.tableHeader,
      field: id,
      ...c,
    }));

    if (showRowSelectCheckbox) {
      allColumns.unshift({
        field: 'select',
        label: (
          <input type='checkbox' name='selectAll'
            checked={items?.length && rowSelectionModel?.length === items?.length}
            onChange={handleSelectAll}
          />
        ),
        type: 'select',
        align: 'center',
        disableSorting: true,
        maxWidth: '20px',
        getActions: (params) => getListActions({
          params, rowSelection: true, rowSelectionModel
        })
      })
    }

    if (actions) {
      allColumns.push({
        field: 'actions',
        label: 'Actions',
        type: "actions",
        align: 'center',
        disableSorting: true,
        minWidth: 'content-fit',
        actionGrid: 1,
        headerClassName: classes.tableHeader,
        getActions: (params) => getListActions({
          params, editItem, entity, editActionIcon,
          customActions: actions, hideEditAction, hideDeleteAction: true,
          editActionLabel, hideArchiveAction , personalizedList
        }),
      });
    }

    if (menuOptions) {
      allColumns.push({
        field: 'menu',
        label: '',
        type: "actions",
        align: 'right',
        minWidth: 45,
        disableSorting: true,
        headerClassName: classes.tableHeader,
        getActions: (params) => getMenuListActions({ params, menuOptions, archiveItem })
      });
    }

    return allColumns;
  }, [filteredColumns, hideEditAction, hideDeleteAction, rowSelectionModel]);

  React.useEffect(() => {
    if (searchParams) {
      for (const { field, type } of filters) {
        const value = searchParams.get(field)
        
        if (value && (type !== 'select' || (filterOptions[field]))) {
          handleFilterChange({ field, value })
        }
      }
    }
  }, [searchParams, filters, filterOptions]);

  React.useEffect(() => {
    if (refreshData) refresh();
    setRefresh(false)
  }, [refreshData]);

  const [searchInput, setSearchInput] = React.useState(pageFilters?.[pathname]?.filterByName || '');
  const [hasInteracted, setHasInteracted] = React.useState(!!(pageFilters?.[pathname]?.filterByName || ''));

  // UI rendering logic
  if (hideColumnSelection) {
    // Old filter UI
    return (
      <Box>
        {(quickSearchField || filters.length > 0) && (
          <Filter
            filters={filters}
            filterOptions={filterOptions}
            onChange={handleFilterChange}
            appliedFilters={appliedFilters}
            quickSearchField={quickSearchField}
            personalizedList={personalizedList}
            columns={columns}
            visibleColumns={visibleColumns}
            setVisibleColumns={setVisibleColumns}
          >
            <Box className={classes.filterItems}>
              {quickSearchField && (
                <Box display='flex' gap={1} alignItems='center'>
                  <DebouncedTextField
                    placeholder="Search..."
                    defaultValue={searchInput}
                    startIcon
                    onChange={(value) => { setSearchInput(value); if (!hasInteracted) setHasInteracted(true); }}
                  />
                  <CustomButton
                    variant="contained"
                    size="medium"
                    disabled={!hasInteracted && (!searchInput || !searchInput.trim())}
                    onClick={() => handleFilterChange({ field: 'filterByName', value: searchInput.trim() })}
                  >
                    Search
                  </CustomButton>
                </Box>
              )}
              {
                props.newFilter &&
                props.newFilter
              }
            </Box>
          </Filter>
        )}
        <TableSection
          _columns={_columns}
          items={items}
          showDummyRows={showDummyRows}
          dummyRows={dummyRows}
          actions={actions}
          handleRowSelection={handleRowSelection}
          showRowSelectCheckbox={showRowSelectCheckbox}
          rowSelectionModel={rowSelectionModel}
          onRowClick={onRowClick}
          appliedFilters={appliedFilters}
          noRowsMessage={noRowsMessage}
          loading={loading}
          total={total}
          pageSize={pageSize}
          setPageNo={setPageNo}
          pageNo={pageNo}
          height={height}
          classes={classes}
          sx={sx}
          pageFilters={pageFilters}
          pathname={pathname}
          handleSortModelChange={handleSortModelChange}
        />
      </Box>
    );
  }

  // New UI (as previously implemented)
  return (
    <Box>
      {/* Top row: search and buttons */}
      <Box display='flex' flexDirection='row' alignItems='center' justifyContent='space-between' width='100%' mb={2}>
        {/* Left: Search */}
        <Box flex={1}>
          {quickSearchField && (
            <Box display='flex' gap={1} alignItems='center'>
              <DebouncedTextField
                placeholder="Search here..."
                defaultValue={searchInput}
                startIcon
                onChange={(value) => { setSearchInput(value); if (!hasInteracted) setHasInteracted(true); }}
              />
              <CustomButton
                variant="contained"
                size='medium'
                disabled={!hasInteracted && (!searchInput || !searchInput.trim())}
                onClick={() => handleFilterChange({ field: 'filterByName', value: searchInput.trim() })}
              >
                Search
              </CustomButton>
            </Box>
          )}
        </Box>
        {/* Right: Three buttons */}
        <Box display='flex' flexDirection='row' alignItems='center' gap={2} ml={2}>
          {/* Category Button and Menu */}
          <Box position='relative'>
            <CustomButton
              disableElevation
              id="category-button"
              variant="outlined"
              aria-controls={categoryOpen ? 'category-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={categoryOpen ? 'true' : undefined}
              onClick={handleCategoryClick}
              startIcon={<AddRoundedIcon />}
              size='medium'
            >
              {categoryLabel}
              <ArrowDropDownIcon fontSize="14px"/>
            </CustomButton>
            <Menu
              id="category-menu"
              anchorEl={categoryAnchorEl}
              open={categoryOpen}
              onClose={handleCategoryClose}
              MenuListProps={{
                'aria-labelledby': 'category-button',
              }}
            >
              <Box id='categories'>
                <Box display='flex' flexDirection='column' gap='4px' px='6px' fontSize='12px'>
                  {columns.map((col, index) => {
                    const isRequired = index < REQUIRED_COLUMNS_COUNT;
                    return (
                      <label 
                        key={index} 
                        htmlFor={`filter${col.field || col.id}`} 
                        style={{
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '6px', 
                          marginBottom: 2,
                          opacity: isRequired ? 0.6 : 1,
                          cursor: isRequired ? 'not-allowed' : 'pointer'
                        }}
                      >
                        <input
                          checked={visibleColumns.includes(col.field || col.id)}
                          onChange={() => handleCategoryToggle(col.field || col.id)}
                          type='checkbox'
                          id={`filter${col.field || col.id}`}
                          name={col.field || col.id}
                          disabled={isRequired}
                          style={{cursor: isRequired ? 'not-allowed' : 'pointer'}}
                        />
                        <Typography 
                          variant='body02-semiBold'
                          style={{color: isRequired ? '#888' : 'inherit'}}
                        >
                          {col.label} {isRequired ? '(Required)' : ''}
                        </Typography>
                      </label>
                    );
                  })}
                </Box>
              </Box>
            </Menu>
          </Box>
          <Box position='relative'>
            <CustomButton
              variant="contained"
              size='medium'
              onClick={handleExportClick}
              disabled={!onExport || items.length === 0}
              startIcon={<IosShareOutlinedIcon />}
            >
               Export performance
            </CustomButton>
            <Menu
              id="export-menu"
              anchorEl={exportAnchorEl}
              open={exportOpen}
              onClose={handleExportClose}
              MenuListProps={{
                'aria-labelledby': 'export-button',
              }}
            >
              <Box sx={{ minWidth: 250, p: 2 }}>
                <Typography variant="h6-medium" sx={{ mb: 2 }}>
                  Select columns to export
                </Typography>
                <Box display='flex' flexDirection='column' gap='8px' mb={2}>
                  {columns.map((col, index) => (
                    <label key={index} htmlFor={`export${col.field || col.id}`} style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                      <input
                        checked={exportColumns.includes(col.field || col.id)}
                        onChange={() => handleExportToggle(col.field || col.id)}
                        type='checkbox'
                        id={`export${col.field || col.id}`}
                        name={col.field || col.id}
                      />
                      <Typography variant='body02-medium'>
                        {col.label}
                      </Typography>
                    </label>
                  ))}
                </Box>
                <CustomButton
                  variant="contained"
                  size="small"
                  onClick={handleExportSubmit}
                  disabled={exportColumns.length === 0}
                  sx={{ width: '100%' }}
                >
                  Export CSV
                </CustomButton>
              </Box>
            </Menu>
          </Box>
        </Box>
      </Box>
      {/* Applied filters row */}
      <Box mt={2} width='100%'>
        
        <Box display='flex' gap='12px' alignItems='center' flexWrap='wrap' style={{overflowX: 'auto', maxWidth: '100%' , marginTop: '10px' }}>
        <Typography variant="body01-bold">Applied filters:</Typography>
          {appliedColumnChips.map((tag, index) => {
            const isRequired = index < REQUIRED_COLUMNS_COUNT;
            return (
              <Chip
                key={tag.id}
                content={tag.label}
                onClick={isRequired ? undefined : () => handleRemoveColumn(tag.id)}
                bgColor="#F5F9FF"
                typographyVariant='body01-semiBold'
                endIcon={
                  !isRequired ? (
                    <img src='https://languify-assets-v02.s3.ap-south-1.amazonaws.com/images/cancel-icon.png' alt='close' width={20} height={20}/>
                  ) : null
                }
                style={{
                  cursor: isRequired ? 'default' : 'pointer',
                  opacity: isRequired ? 0.7 : 1
                }}
                sx={{borderRadius: '12px'}}
              />
            );
          })}
        </Box>
      </Box>
      <TableSection
        _columns={_columns}
        items={items}
        showDummyRows={showDummyRows}
        dummyRows={dummyRows}
        actions={actions}
        handleRowSelection={handleRowSelection}
        showRowSelectCheckbox={showRowSelectCheckbox}
        rowSelectionModel={rowSelectionModel}
        onRowClick={onRowClick}
        appliedFilters={appliedFilters}
        noRowsMessage={noRowsMessage}
        loading={loading}
        total={total}
        pageSize={pageSize}
        setPageNo={setPageNo}
        pageNo={pageNo}
        height={height}
        classes={classes}
        sx={sx}
        pageFilters={pageFilters}
        pathname={pathname}
        handleSortModelChange={handleSortModelChange}
      />
    </Box>
  );
}
